import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function BillingReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed
  const [meta, setMeta] = useState(null);
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setMeta(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("expired");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) setStatus("failed");
        else setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center bg-card border border-border rounded-2xl p-10">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
            <h1 className="font-serif text-3xl mt-5">Finalizing…</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Confirming your subscription with Stripe.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-primary" />
            <h1 className="font-serif text-3xl mt-5">You're in.</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Your {meta?.package_id} plan is active. Head to your dashboard to
              manage your menu.
            </p>
            <button
              data-testid="billing-return-dashboard"
              onClick={() => navigate("/dispensary/dashboard")}
              className="mt-6 h-11 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Go to dashboard
            </button>
          </>
        )}
        {status === "expired" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-amber-600" />
            <h1 className="font-serif text-3xl mt-5">Session expired</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Payment took too long to confirm. Try again — no charge yet.
            </p>
            <Link
              to="/pricing"
              data-testid="billing-return-retry"
              className="mt-6 inline-flex h-11 px-6 items-center rounded-full bg-foreground text-background"
            >
              Back to plans
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="font-serif text-3xl mt-5">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mt-2">
              We couldn't confirm your payment. Please try again or contact
              support.
            </p>
            <Link
              to="/pricing"
              data-testid="billing-return-retry"
              className="mt-6 inline-flex h-11 px-6 items-center rounded-full bg-foreground text-background"
            >
              Back to plans
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
